import { devLog, devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';

import React, { useState, useEffect } from 'react';
import { Button, Container, Table, Row, Col, Card, Badge, Tab, Tabs, Alert, ProgressBar, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth, db } from '../firebase';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import ProgressReportGenerator from '../components/ProgressReportGenerator';
import DailyReportLog from '../components/DailyReportLog';
import CourseBuilder from '../components/CourseBuilder';
import SessionLogger from '../components/SessionLogger';
import ModuleBuilder from '../components/ModuleBuilder';
import FacilitatorCohortManagement from '../components/FacilitatorCohortManagement';
import QuizBuilder from '../components/QuizBuilder';
import GoogleMeetLinks from '../components/GoogleMeetLinks';
import SessionRSVPAdmin from '../components/SessionRSVPAdmin';
import SessionPollAdmin from '../components/SessionPollAdmin';
import FacilitatorLearnerLookup from '../components/FacilitatorLearnerLookup';
import enhancedFacilitatorService, { FacilitatorServiceError } from '../services/enhancedFacilitatorService';
import facilitatorService from '../services/facilitatorService';
import adminDocumentUploadService from '../services/adminDocumentUploadService';
import programsService from '../services/programsService';
import attendanceService from '../services/attendanceService';
import { DOCUMENT_CATEGORIES } from '../config/documentCategories';
import { collection, query, orderBy, limit, getDocs, where, documentId } from 'firebase/firestore';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const FacilitatorDashboard = () => {
  usePageTitle("Facilitator Dashboard");
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [issues, setIssues] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [historicCohortId, setHistoricCohortId] = useState('');
  const [historicSessionDate, setHistoricSessionDate] = useState('');
  const [historicSessionTime, setHistoricSessionTime] = useState('');
  const [historicSessionTitle, setHistoricSessionTitle] = useState('');
  const [historicSessionNotes, setHistoricSessionNotes] = useState('');
  const [savingHistoricSession, setSavingHistoricSession] = useState(false);
  const [historicSessionSuccess, setHistoricSessionSuccess] = useState('');

  // Document management state
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [documentSearchResults, setDocumentSearchResults] = useState([]);
  const [documentSearchLoading, setDocumentSearchLoading] = useState(false);
  const [documentSearchError, setDocumentSearchError] = useState('');
  const [documentProgramFilter, setDocumentProgramFilter] = useState('');
  const [documentCohortFilter, setDocumentCohortFilter] = useState('');
  const [teamsSessionId, setTeamsSessionId] = useState('');
  const [teamsCsvRows, setTeamsCsvRows] = useState([]);
  const [teamsCheckResults, setTeamsCheckResults] = useState(null);
  const [teamsCheckError, setTeamsCheckError] = useState('');
  const [teamsCheckLoading, setTeamsCheckLoading] = useState(false);

  const chunkArray = (items, size) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const normalizeHeader = (value) => (
    String(value || '').toLowerCase().replace(/[^a-z]/g, '')
  );

  const normalizeName = (value) => (
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );

  const splitCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map((value) => value.trim());
  };

  const splitTeamsLine = (line) => {
    if (line.includes('\t')) {
      return line.split('\t').map((value) => value.trim());
    }
    return splitCsvLine(line);
  };

  const parseTeamsCsvText = (text) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];

    const participantsIndex = lines.findIndex((line) => (
      /^\s*2\.\s*participants\s*$/i.test(line) || /^\s*participants\s*$/i.test(line)
    ));

    let headerLineIndex = -1;
    if (participantsIndex >= 0) {
      for (let i = participantsIndex + 1; i < lines.length; i += 1) {
        if (lines[i].trim()) {
          headerLineIndex = i;
          break;
        }
      }
    } else {
      headerLineIndex = lines.findIndex((line) => line.trim().length > 0);
    }

    if (headerLineIndex < 0) return [];

    const headers = splitTeamsLine(lines[headerLineIndex]);
    const headerMap = headers.reduce((acc, header, index) => {
      acc[normalizeHeader(header)] = index;
      return acc;
    }, {});

    const nameKeys = ['fullname', 'name', 'displayname', 'username', 'participantname'];
    const emailKeys = ['email', 'emailaddress', 'useremail', 'mail'];

    const nameIndex = nameKeys.map((key) => headerMap[key]).find((value) => value !== undefined);
    const emailIndex = emailKeys.map((key) => headerMap[key]).find((value) => value !== undefined);

    const rows = [];
    for (let i = headerLineIndex + 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.trim()) break;
      if (/^\s*3\./i.test(line) || /in-?meeting activities/i.test(line)) break;
      const columns = splitTeamsLine(line);
      const name = nameIndex !== undefined ? columns[nameIndex] : '';
      const email = emailIndex !== undefined ? columns[emailIndex] : '';
      if (!name && !email) continue;
      rows.push({ name, email });
    }
    return rows;
  };

  useEffect(() => {
    if (currentUser) {
      loadDashboardData();
    }

    // Listen for document upload events to refresh dashboard data
    const handleDocumentUploaded = (event) => {
      devLog('Document uploaded event received, refreshing dashboard data...');
      devLog('Event details:', event.detail);
      loadDashboardData(); // Refresh all learner data
    };

    window.addEventListener('documentUploaded', handleDocumentUploaded);

    return () => {
      window.removeEventListener('documentUploaded', handleDocumentUploaded);
    };
  }, [currentUser]);

  const hydrateLearnerDocuments = async (learnerList) => {
    if (!Array.isArray(learnerList) || learnerList.length === 0) return;
    try {
      const updated = [];
      const chunkSize = 10;
      for (let i = 0; i < learnerList.length; i += chunkSize) {
        const chunk = learnerList.slice(i, i + chunkSize);
        const docsResults = await Promise.all(
          chunk.map((learner) =>
            facilitatorService.getLearnerDocuments(learner.id).catch(() => ({}))
          )
        );
        chunk.forEach((learner, index) => {
          updated.push({ ...learner, documents: docsResults[index] });
        });
      }
      setLearners((prev) => {
        const docMap = new Map(updated.map((learner) => [learner.id, learner.documents]));
        return prev.map((learner) => ({
          ...learner,
          documents: docMap.get(learner.id) || learner.documents
        }));
      });
    } catch (error) {
      devLog('Background document hydration failed:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [learnersData, modulesData, assignmentsData, programsData] = await Promise.all([
        // Get learners from facilitator's assigned programs
        facilitatorService.getLearnersByAssignedPrograms(currentUser.uid, { includeDocuments: false }),
        enhancedFacilitatorService.getModules(currentUser.uid),
        Promise.resolve([]), // Will load assignments per module later
        programsService.getPrograms(true) // Get active programs
      ]);

      const isActiveLearner = (learner) => {
        if (!learner) return false;
        if (learner.deleted || learner.deactivated) return false;
        if (learner.isActive === false) return false;
        const status = String(learner.status || '').toLowerCase();
        if (['inactive', 'dropoff', 'drop_off', 'drop off', 'dropped'].includes(status)) return false;
        return true;
      };

      let scopedLearners = learnersData;
      if (userProfile?.role === 'facilitator') {
        const assignedCohorts = userProfile.assignedCohorts || [];
        const assignedPrograms = userProfile.assignedPrograms || [];
        if (assignedCohorts.length > 0) {
          scopedLearners = learnersData.filter((learner) => assignedCohorts.includes(learner.cohortId));
        } else if (assignedPrograms.length > 0) {
          scopedLearners = learnersData.filter((learner) => assignedPrograms.includes(learner.programId));
        }
      }

      const activeLearners = scopedLearners.filter(isActiveLearner);
      setLearners(activeLearners);
      setModules(modulesData);
      setAssignments(assignmentsData);
      setPrograms(programsData);

      const assignedCohortIds = (userProfile?.assignedCohorts?.length
        ? userProfile.assignedCohorts
        : Array.from(new Set(scopedLearners.map((learner) => learner.cohortId).filter(Boolean))));

      if (assignedCohortIds.length > 0) {
        const cohortChunks = chunkArray(assignedCohortIds, 10);
        const cohortDocs = [];
        for (const chunk of cohortChunks) {
          const snapshot = await getDocs(query(collection(db, 'cohorts'), where(documentId(), 'in', chunk)));
          snapshot.docs.forEach((docSnap) => {
            cohortDocs.push({ id: docSnap.id, ...docSnap.data() });
          });
        }
        setCohorts(cohortDocs);
      } else {
        setCohorts([]);
      }

      const scheduleHydration = window.requestIdleCallback || ((fn) => setTimeout(fn, 250));
      scheduleHydration(() => hydrateLearnerDocuments(scopedLearners));
    } catch (error) {
      devError('Error loading dashboard data:', error);

      if (error instanceof FacilitatorServiceError) {
        devError('Facilitator service error:', error.code, error.message);
      }
    } finally {
      setLoading(false);
    }

    // Load non-critical data after primary dashboard content renders
    Promise.all([
      getDocs(query(collection(db, 'issues'), orderBy('createdAt', 'desc'), limit(5)))
        .then(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        .catch(() => []),
      facilitatorService.getResources(currentUser.uid).catch(() => [])
    ]).then(([issuesData, resourcesData]) => {
      setIssues(issuesData);
      setResources(resourcesData);
    });
  };

  const loadSessions = async (cohortList) => {
    if (!cohortList.length) {
      setSessions([]);
      return;
    }

    try {
      setLoadingSessions(true);
      setSessionError('');
      const sessionResults = await Promise.all(
        cohortList.map((cohort) => attendanceService.getSessionsForCohort(cohort.id))
      );
      const mergedSessions = sessionResults
        .filter((result) => result.success)
        .flatMap((result) => result.sessions || [])
        .map((session) => ({
          ...session,
          cohortName: session.cohortName || cohortList.find(c => c.id === session.cohortId)?.name || 'Cohort'
        }))
        .sort((a, b) => {
          const aDate = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
          const bDate = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
          return bDate - aDate;
        });
      setSessions(mergedSessions);
    } catch (error) {
      devError('Error loading sessions:', error);
      setSessionError('Failed to load sessions.');
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    if (cohorts.length > 0) {
      loadSessions(cohorts);
    }
  }, [cohorts]);

  const handleTeamsCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setTeamsCheckError('');
    setTeamsCheckResults(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || '';
      const rows = parseTeamsCsvText(String(text));
      setTeamsCsvRows(rows);
    };
    reader.onerror = () => {
      setTeamsCheckError('Failed to read CSV file.');
    };
    reader.readAsText(file);
  };

  const handleCreateHistoricSession = async () => {
    if (!historicCohortId) {
      setSessionError('Please select a cohort for the session.');
      return;
    }
    if (!historicSessionDate) {
      setSessionError('Please select a session date.');
      return;
    }
    if (!historicSessionTime) {
      setSessionError('Please select a session time.');
      return;
    }

    const scheduledDate = new Date(`${historicSessionDate}T${historicSessionTime}`);
    if (Number.isNaN(scheduledDate.getTime())) {
      setSessionError('Invalid session date or time.');
      return;
    }

    const cohortName = cohorts.find(c => c.id === historicCohortId)?.name || 'Cohort';
    const defaultTitle = `Session - ${scheduledDate.toLocaleDateString()}`;
    const sessionTitle = historicSessionTitle || defaultTitle;

    try {
      setSavingHistoricSession(true);
      setSessionError('');
      setHistoricSessionSuccess('');

      const result = await attendanceService.createSession({
        cohortId: historicCohortId,
        moduleId: null,
        moduleName: 'Session',
        scheduledDate,
        facilitatorId: currentUser?.uid || null,
        createdBy: currentUser?.uid || 'facilitator',
        title: sessionTitle,
        notes: historicSessionNotes?.trim() || '',
        cohortName,
        status: 'scheduled',
        requiresAttendanceConfirmation: false,
        confirmationRequested: false
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create session.');
      }

      setHistoricSessionSuccess('Session created. You can now select it below for the Teams check.');
      setTeamsSessionId(result.sessionId);
      setHistoricSessionTitle('');
      setHistoricSessionNotes('');
      await loadSessions(cohorts);
    } catch (error) {
      devError('Error creating session:', error);
      setSessionError('Failed to create session: ' + error.message);
    } finally {
      setSavingHistoricSession(false);
    }
  };

  const runTeamsAttendanceCheck = async () => {
    try {
      setTeamsCheckLoading(true);
      setTeamsCheckError('');

      if (!teamsSessionId) {
        setTeamsCheckError('Please select a session.');
        return;
      }
      if (teamsCsvRows.length === 0) {
        setTeamsCheckError('Please upload a Google Meet attendance CSV file.');
        return;
      }

      const selectedSession = sessions.find((session) => session.id === teamsSessionId);
      if (!selectedSession?.cohortId) {
        setTeamsCheckError('Selected session is missing a cohort.');
        return;
      }

      const cohortLearners = learners.filter((learner) => learner.cohortId === selectedSession.cohortId);
      if (cohortLearners.length === 0) {
        setTeamsCheckError('No learners found for the selected session cohort.');
        return;
      }

      const expectedByEmail = new Map();
      const expectedByName = new Map();
      cohortLearners.forEach((learner) => {
        const email = String(learner.email || '').toLowerCase();
        const name = normalizeName(learner.displayName || learner.name || '');
        if (email) expectedByEmail.set(email, learner);
        if (name) expectedByName.set(name, learner);
      });

      const matchedLearnerIds = new Set();
      const presentExpected = [];
      const unexpectedAttendees = [];

      teamsCsvRows.forEach((row) => {
        const email = String(row.email || '').toLowerCase();
        const nameKey = normalizeName(row.name);
        const matchedLearner = email ? expectedByEmail.get(email) : expectedByName.get(nameKey);

        if (matchedLearner) {
          if (!matchedLearnerIds.has(matchedLearner.id)) {
            matchedLearnerIds.add(matchedLearner.id);
            presentExpected.push({
              name: matchedLearner.displayName || matchedLearner.name || row.name || 'Unknown',
              email: matchedLearner.email || row.email || '',
              matchType: email && expectedByEmail.get(email) ? 'email' : 'name'
            });
          }
        } else {
          unexpectedAttendees.push({
            name: row.name || 'Unknown',
            email: row.email || '',
            reason: row.email ? 'Not in cohort' : 'Unmatched name'
          });
        }
      });

      const missingExpected = cohortLearners
        .filter((learner) => !matchedLearnerIds.has(learner.id))
        .map((learner) => ({
          name: learner.displayName || learner.name || 'Unknown',
          email: learner.email || ''
        }));

      setTeamsCheckResults({
        cohortName: selectedSession.cohortName || cohorts.find(c => c.id === selectedSession.cohortId)?.name || 'Cohort',
        sessionTitle: selectedSession.title || 'Session',
        sessionDate: selectedSession.scheduledDate || null,
        totalExpected: cohortLearners.length,
        totalAttendees: teamsCsvRows.length,
        presentExpected,
        missingExpected,
        unexpectedAttendees
      });
    } catch (error) {
      devError('Error running Meet attendance check:', error);
      setTeamsCheckError('Failed to run Meet attendance check.');
    } finally {
      setTeamsCheckLoading(false);
    }
  };

  const exportTeamsAttendancePdf = () => {
    if (!teamsCheckResults) return;
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text('Teams Attendance Cohort Check', 14, 18);
    doc.setFontSize(11);
    doc.text(`Cohort: ${teamsCheckResults.cohortName}`, 14, 26);
    if (teamsCheckResults.sessionTitle) {
      doc.text(`Session: ${teamsCheckResults.sessionTitle}`, 14, 32);
    }
    if (teamsCheckResults.sessionDate) {
      const formattedDate = teamsCheckResults.sessionDate?.toLocaleString
        ? teamsCheckResults.sessionDate.toLocaleString()
        : new Date(teamsCheckResults.sessionDate).toLocaleString();
      doc.text(`Date: ${formattedDate}`, 14, 38);
    }
    doc.text(`Expected: ${teamsCheckResults.totalExpected}`, 14, 46);
    doc.text(`Attendees in CSV: ${teamsCheckResults.totalAttendees}`, 14, 52);

    autoTable(doc, {
      startY: 60,
      head: [['Present (Expected)', 'Email', 'Match']],
      body: teamsCheckResults.presentExpected.map((row) => [row.name, row.email, row.matchType])
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [['Missing (Expected)', 'Email']],
      body: teamsCheckResults.missingExpected.map((row) => [row.name, row.email])
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [['Unexpected Attendees', 'Email', 'Reason']],
      body: teamsCheckResults.unexpectedAttendees.map((row) => [row.name, row.email, row.reason])
    });

    doc.save(`teams_attendance_check_${teamsCheckResults.cohortName.replace(/[^a-z0-9]+/gi, '_')}.pdf`);
  };

  const getEffectiveDocumentStatus = (docData = {}) => {
    if (docData.reviewStatus === 'qa_approved') return 'verified';
    if (docData.reviewStatus === 'qa_rejected') return 'rejected';
    if (docData.status === 'approved') return 'verified';
    return docData.status || 'pending';
  };

  const getTotalRequiredDocumentsForLearner = (learner) => {
    const learnerProgram = programs.find((program) => program.id === learner?.programId);
    const configuredRequired = Array.isArray(learnerProgram?.requiredDocuments)
      ? learnerProgram.requiredDocuments.filter(Boolean).length
      : 0;

    return configuredRequired > 0 ? configuredRequired : 11;
  };

  const getLearnerProgress = (learner) => {
    // Calculate real document progress from learner's documents
    const documents = learner.documents || {};
    const totalRequiredDocs = getTotalRequiredDocumentsForLearner(learner);

    // Count verified documents for completion rate
    let verifiedDocs = 0;
    let missingDocs = 0;

    // Count document statuses
    let pendingDocuments = 0;
    let rejectedDocuments = 0;

    Object.values(documents).forEach(doc => {
      const effectiveStatus = getEffectiveDocumentStatus(doc);
      if (effectiveStatus === 'verified') {
        verifiedDocs++;
      } else if (effectiveStatus === 'pending' || effectiveStatus === 'submitted' || effectiveStatus === 'pending_verification') {
        pendingDocuments++;
      } else if (effectiveStatus === 'rejected') {
        rejectedDocuments++;
      }
    });

    // Calculate missing documents (total required - verified - pending - rejected)
    missingDocs = totalRequiredDocs - verifiedDocs - pendingDocuments - rejectedDocuments;
    missingDocs = Math.max(0, missingDocs); // Ensure it's not negative

    const documentCompletionRate = Math.round((verifiedDocs / totalRequiredDocs) * 100);

    // Debug logging
    if (Object.keys(documents).length > 0) {
      devLog(`📊 Document progress for ${learner.displayName}:`, {
        totalDocs: Object.keys(documents).length,
        verified: verifiedDocs,
        pending: pendingDocuments,
        rejected: rejectedDocuments,
        missing: missingDocs,
        completionRate: documentCompletionRate,
        documentKeys: Object.keys(documents),
        statuses: Object.values(documents).map(d => d.status)
      });
    }

    // Outstanding documents are only missing and rejected (not pending - those are submitted)
    const outstandingDocuments = missingDocs + rejectedDocuments;

    // Calculate real module completion rate from learner data
    const moduleProgress = learner.moduleProgress || {};
    const programModules = learner.programId ?
      programs.find(p => p.id === learner.programId)?.modules || [] : [];

    let completedModules = 0;
    if (programModules.length > 0) {
      completedModules = programModules.filter(moduleId => {
        const progress = moduleProgress[moduleId];
        return progress && progress.completed === true;
      }).length;
    }

    const moduleCompletionRate = programModules.length > 0 ?
      Math.round((completedModules / programModules.length) * 100) : 0;

    // Calculate real assignment completion rate from learner data
    const assignments = learner.assignments || {};
    const submittedAssignments = Object.values(assignments).filter(
      assignment => assignment.status === 'submitted' || assignment.status === 'graded'
    ).length;
    const totalAssignments = Object.keys(assignments).length;

    const assignmentCompletionRate = totalAssignments > 0 ?
      Math.round((submittedAssignments / totalAssignments) * 100) : 0;

    return {
      moduleCompletionRate,
      assignmentCompletionRate,
      documentCompletionRate,
      outstandingDocuments,
      pendingDocuments,
      rejectedDocuments,
      // A learner has outstanding items if they have missing, rejected, OR pending documents
      // Only show "Complete" when documentCompletionRate is 100% (all verified)
      hasOutstandingItems: outstandingDocuments > 0 || rejectedDocuments > 0 || pendingDocuments > 0 || documentCompletionRate < 100
    };
  };

  const getLearnersWithOutstandingDocuments = () => {
    return learners.filter(learner => {
      const progress = getLearnerProgress(learner);
      return progress.hasOutstandingItems;
    });
  };

  // Calculate dashboard statistics from real learner data
  const calculateDashboardStats = () => {
    const totalLearners = learners.length;
    const activeLearners = learners.filter(learner =>
      learner.isActive === true || learner.status === 'active'
    ).length;

    let totalOutstandingDocs = 0;
    let totalSubmittedDocs = 0;

    learners.forEach(learner => {
      const progress = getLearnerProgress(learner);
      totalOutstandingDocs += progress.outstandingDocuments;
      // Count verified and pending documents as submitted
      const documents = learner.documents || {};
      let submittedCount = 0;
      Object.values(documents).forEach(doc => {
        const effectiveStatus = getEffectiveDocumentStatus(doc);
        if (effectiveStatus === 'verified' || effectiveStatus === 'pending' || effectiveStatus === 'submitted' || effectiveStatus === 'pending_verification') {
          submittedCount++;
        }
      });
      totalSubmittedDocs += submittedCount;
    });

    return {
      totalLearners,
      activeLearners,
      totalOutstandingDocs,
      totalSubmittedDocs
    };
  };

  // Get program name from program ID
  const getProgramName = (learner) => {
    const programId = learner?.programId;
    if (!programId) return learner?.programName || learner?.program || 'Not assigned';
    const program = programs.find(p => p.id === programId);
    return program?.name || learner?.programName || learner?.program || 'Unknown Program';
  };

  const getCohortName = (cohortId) => {
    if (!cohortId) return 'Not assigned';
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort?.name || cohort?.cohortName || cohortId;
  };

  const getScopedPrograms = () => {
    if (userProfile?.role !== 'facilitator' && userProfile?.role !== 'external_facilitator') {
      return programs;
    }
    const assignedPrograms = userProfile?.assignedPrograms || [];
    if (assignedPrograms.length === 0) {
      return programs;
    }
    return programs.filter(program => assignedPrograms.includes(program.id));
  };

  const getScopedCohorts = () => {
    if (!documentProgramFilter) {
      return cohorts;
    }
    return cohorts.filter(cohort => cohort.programId === documentProgramFilter);
  };

  const handleSearchDocuments = async () => {
    const searchValue = documentSearchTerm.trim().toLowerCase();
    const shouldSearchByTerm = searchValue.length >= 2;
    const shouldSearchByFilter = documentProgramFilter || documentCohortFilter;

    if (!shouldSearchByTerm && !shouldSearchByFilter) {
      setDocumentSearchError('Enter at least 2 characters or select a cohort/program to search.');
      setDocumentSearchResults([]);
      return;
    }

    try {
      setDocumentSearchLoading(true);
      setDocumentSearchError('');
      const filteredLearners = learners.filter((learner) => {
        if (documentProgramFilter && learner.programId !== documentProgramFilter) {
          return false;
        }
        if (documentCohortFilter && learner.cohortId !== documentCohortFilter) {
          return false;
        }
        if (!shouldSearchByTerm) {
          return true;
        }
        const email = (learner.email || '').toLowerCase();
        const displayName = (learner.displayName || '').toLowerCase();
        const idNumber = (learner.idNumber || '').toLowerCase();
        const firstName = (learner.firstName || '').toLowerCase();
        const lastName = (learner.lastName || '').toLowerCase();
        return (
          email.includes(searchValue) ||
          displayName.includes(searchValue) ||
          idNumber.includes(searchValue) ||
          firstName.includes(searchValue) ||
          lastName.includes(searchValue)
        );
      });

      const results = [];
      for (const learner of filteredLearners) {
        const documentsSnapshot = await getDocs(collection(db, 'users', learner.id || learner.uid, 'documents'));
        documentsSnapshot.docs.forEach((docSnap) => {
          const docData = docSnap.data();
          if (docData.deleted === true) {
            return;
          }
          results.push({
            id: docSnap.id,
            learnerId: learner.id || learner.uid,
            learnerName: learner.displayName || 'Unknown',
            learnerEmail: learner.email || '',
            programName: getProgramName(learner),
            cohortName: getCohortName(learner.cohortId),
            category: docData.category || docData.categoryName || docData.documentType || 'Document',
            status: getEffectiveDocumentStatus(docData),
            uploadedAt: docData.uploadedAt?.toDate ? docData.uploadedAt.toDate() : (docData.uploadDate?.toDate ? docData.uploadDate.toDate() : null),
            fileURL: docData.fileURL || docData.downloadURL || '',
            uploadedByRole: docData.uploadedByRole || (docData.adminUpload ? 'facilitator' : 'learner'),
            uploadedByEmail: docData.uploadedByEmail || '',
            rejectionReason: docData.rejectionReason || '',
            reviewNotes: docData.reviewNotes || ''
          });
        });
      }

      const sorted = results.sort((a, b) => {
        const aTime = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const bTime = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        return bTime - aTime;
      });
      setDocumentSearchResults(sorted);
    } catch (error) {
      devError('Error searching documents:', error);
      setDocumentSearchError('Failed to search documents. Please try again.');
      setDocumentSearchResults([]);
    } finally {
      setDocumentSearchLoading(false);
    }
  };

  const exportLoginReport = () => {
    try {
      // Prepare data for export
      const reportData = learners.map(learner => {
        const formatLastLoginForExport = (lastLoginAt, createdAt) => {
          if (!lastLoginAt) return 'Never';

          const lastLogin = lastLoginAt.toDate ? lastLoginAt.toDate() : new Date(lastLoginAt);

          // Validate that lastLogin is a valid date
          if (isNaN(lastLogin.getTime())) {
            return 'Invalid Date';
          }

          // NOTE: Removed the createdAt proximity check that was causing false "Never" in exports
          // The lastLoginAt field is maintained by AuthContext and represents actual login time

          return lastLogin.toLocaleString('en-ZA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        };

        const lastLoginStatus = formatLastLoginForExport(learner.lastLoginAt, learner.createdAt);
        const createdDate = learner.createdAt
          ? (learner.createdAt.toDate ? learner.createdAt.toDate() : new Date(learner.createdAt)).toLocaleDateString('en-ZA')
          : 'N/A';

        return {
          'Name': learner.displayName || 'N/A',
          'Email': learner.email || 'N/A',
          'ID Number': learner.idNumber || 'N/A',
          'Program': getProgramName(learner),
          'Cohort': learner.cohortId || 'Not assigned',
          'Account Created': createdDate,
          'Last Sign-In': lastLoginStatus,
          'Sign-In Status': lastLoginStatus === 'Never' ? 'Never Logged In' : 'Has Logged In',
          'Status': learner.status || 'active',
          'Email Verified': learner.emailVerified ? 'Yes' : 'No'
        };
      });

      // Convert to CSV
      if (reportData.length === 0) {
        alert('No learner data available to export');
        return;
      }

      const headers = Object.keys(reportData[0]);
      const csvContent = [
        headers.join(','),
        ...reportData.map(row =>
          headers.map(header => {
            const cell = row[header] || '';
            // Escape commas and quotes in cell content
            return `"${String(cell).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const now = new Date();
      const filename = `learner-login-report-${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      devLog(`Exported ${reportData.length} learner records to ${filename}`);
    } catch (error) {
      devError('Error exporting login report:', error);
      alert('Failed to export login report. Please try again.');
    }
  };

  // Format ID number to ensure 13 digits with leading zeros
  const formatIdNumber = (idNumber) => {
    if (!idNumber) return 'Not provided';
    // Convert to string and pad with leading zeros to make it 13 digits
    const idStr = String(idNumber);
    return idStr.padStart(13, '0');
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  // Document Management Functions
  const handleSearchLearners = async () => {
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      await adminDocumentUploadService.initialize();
      const results = await adminDocumentUploadService.searchLearners(searchTerm, 10);
      setSearchResults(results);
    } catch (error) {
      devError('Error searching learners:', error);
      setUploadError('Failed to search learners: ' + error.message);
    }
  };

  const handleSelectLearnerForUpload = (learner) => {
    setSelectedLearner(learner);
    setShowUploadModal(true);
    setUploadError('');
    setUploadSuccess('');
  };

  const handleFileUpload = async () => {
    if (!selectedLearner || !uploadFile || !selectedCategory) {
      setUploadError('Please select a learner, file, and document category');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');

      const result = await adminDocumentUploadService.uploadDocumentForLearner(
        selectedLearner.id,
        uploadFile,
        selectedCategory,
        {
          notes: uploadNotes,
          reason: 'Facilitator upload'
        }
      );

      setUploadSuccess(result.message);
      setUploadFile(null);
      setUploadNotes('');
      setSelectedCategory('');

      // Close modal after a delay
      setTimeout(() => {
        setShowUploadModal(false);
        setSelectedLearner(null);
        setUploadSuccess('');
      }, 3000);

    } catch (error) {
      devError('Error uploading document:', error);
      setUploadError('Failed to upload document: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedLearner(null);
    setUploadFile(null);
    setUploadNotes('');
    setSelectedCategory('');
    setUploadError('');
    setUploadSuccess('');
  };

  const getResourceIcon = (category, type) => {
    const icons = {
      documents: '📄',
      videos: '🎥',
      links: '🔗',
      templates: '📝',
      tools: '🛠️',
      assessments: '📋',
      presentations: '📊',
      research: '📚'
    };
    return icons[category] || '📁';
  };

  return (
    <>
      <FacilitatorNavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Dashboard</h1>
              <p className="mission-text mb-4">
                Manage your courses and guide learners to success
              </p>
              <p className="mb-0">
                Access course management tools, track learner progress, and facilitate effective learning experiences.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading dashboard data...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading dashboard data...</p>
          </div>
        )}

        {!loading && (
          <>

        {/* Outstanding Documents Alert */}
        {getLearnersWithOutstandingDocuments().length > 0 && (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning">
                <Alert.Heading>📋 Outstanding Documents Alert</Alert.Heading>
                <p>{getLearnersWithOutstandingDocuments().length} learner(s) have outstanding document requirements. Check the Learner Profiles tab for details.</p>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Quick Stats */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{calculateDashboardStats().totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{calculateDashboardStats().activeLearners}</h3>
                <small className="text-muted">Active Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-danger">{calculateDashboardStats().totalOutstandingDocs}</h3>
                <small className="text-muted">Outstanding Docs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{calculateDashboardStats().totalSubmittedDocs}</h3>
                <small className="text-muted">Submitted Docs</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabbed Content */}
        <Tabs defaultActiveKey="attendance" id="facilitator-tabs" className="mb-4">
          <Tab eventKey="rsvps" title="Session RSVPs">
            <SessionRSVPAdmin />
          </Tab>
          <Tab eventKey="polls" title="Session Poll">
            <SessionPollAdmin />
          </Tab>
          <Tab eventKey="teams-meetings" title="📹 Teams Meetings">
            <GoogleMeetLinks />
          </Tab>

          <Tab eventKey="attendance" title="Attendance">
            <>
              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-2">Attendance Reports</h5>
                  <p className="text-muted mb-3">
                    Create attendance confirmations and review attendance analytics from the Attendance Reports page.
                  </p>
                  <Button variant="primary" onClick={() => navigate('/facilitator/attendance')}>
                    Go to Attendance Reports
                  </Button>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-0">Create Historic Session Attendance</h5>
                </Card.Header>
                <Card.Body>
                  {sessionError && (
                    <Alert variant="danger" onClose={() => setSessionError('')} dismissible>
                      {sessionError}
                    </Alert>
                  )}
                  {historicSessionSuccess && (
                    <Alert variant="success" onClose={() => setHistoricSessionSuccess('')} dismissible>
                      {historicSessionSuccess}
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Cohort</Form.Label>
                        <Form.Select
                          value={historicCohortId}
                          onChange={(e) => setHistoricCohortId(e.target.value)}
                        >
                          <option value="">Select cohort...</option>
                          {cohorts.map((cohort) => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name || cohort.id}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={historicSessionDate}
                          onChange={(e) => setHistoricSessionDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={historicSessionTime}
                          onChange={(e) => setHistoricSessionTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Session Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={historicSessionTitle}
                          onChange={(e) => setHistoricSessionTitle(e.target.value)}
                          placeholder="Optional custom title"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={handleCreateHistoricSession}
                        disabled={savingHistoricSession}
                      >
                        {savingHistoricSession ? 'Saving...' : 'Create Session'}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Session Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={historicSessionNotes}
                          onChange={(e) => setHistoricSessionNotes(e.target.value)}
                          placeholder="Add notes for this historic session"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Teams Attendance Cohort Check</h5>
                </Card.Header>
                <Card.Body>
                  {teamsCheckError && (
                    <Alert variant="danger" onClose={() => setTeamsCheckError('')} dismissible>
                      {teamsCheckError}
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Select Session</Form.Label>
                        <Form.Select
                          value={teamsSessionId}
                          onChange={(e) => setTeamsSessionId(e.target.value)}
                          disabled={loadingSessions}
                        >
                          <option value="">Choose a session...</option>
                          {sessions.map((session) => (
                            <option key={session.id} value={session.id}>
                              {(session.title || 'Session')} • {(session.cohortName || 'Cohort')} • {session.scheduledDate ? new Date(session.scheduledDate).toLocaleString() : 'No date'}
                            </option>
                          ))}
                        </Form.Select>
                        {loadingSessions && (
                          <Form.Text className="text-muted">Loading sessions…</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Upload Meet Attendance CSV</Form.Label>
                        <Form.Control type="file" accept=".csv" onChange={handleTeamsCsvUpload} />
                        <Form.Text className="text-muted">
                          CSV should include name or email columns.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={runTeamsAttendanceCheck}
                        disabled={teamsCheckLoading}
                      >
                        {teamsCheckLoading ? 'Checking...' : 'Run Check'}
                      </Button>
                    </Col>
                  </Row>

                  {teamsCsvRows.length > 0 && (
                    <Alert variant="info">
                      Loaded {teamsCsvRows.length} attendance rows from CSV.
                    </Alert>
                  )}

                  {teamsCheckResults && (
                    <>
                      <Row className="mb-3">
                        <Col md={3}>
                          <Card className="text-center bg-light">
                            <Card.Body>
                              <h4 className="text-primary">{teamsCheckResults.totalExpected}</h4>
                              <p className="mb-0 text-muted">Expected Learners</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-success bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-success">{teamsCheckResults.presentExpected.length}</h4>
                              <p className="mb-0 text-muted">Present (Expected)</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-danger bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-danger">{teamsCheckResults.missingExpected.length}</h4>
                              <p className="mb-0 text-muted">Missing</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-warning bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-warning">{teamsCheckResults.unexpectedAttendees.length}</h4>
                              <p className="mb-0 text-muted">Unexpected</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end mb-3">
                        <Button variant="outline-secondary" onClick={exportTeamsAttendancePdf}>
                          Export PDF
                        </Button>
                      </div>

                      <Row className="mb-3">
                        <Col md={6}>
                          <h6>Present (Expected)</h6>
                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamsCheckResults.presentExpected.map((row, index) => (
                                <tr key={`${row.email}-${index}`}>
                                  <td>{row.name}</td>
                                  <td>{row.email}</td>
                                  <td>{row.matchType}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                        <Col md={6}>
                          <h6>Unexpected Attendees</h6>
                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamsCheckResults.unexpectedAttendees.map((row, index) => (
                                <tr key={`${row.email}-${index}`}>
                                  <td>{row.name}</td>
                                  <td>{row.email}</td>
                                  <td>{row.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>

                      <h6>Missing (Expected)</h6>
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamsCheckResults.missingExpected.map((row, index) => (
                            <tr key={`${row.email}-${index}`}>
                              <td>{row.name}</td>
                              <td>{row.email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>
            </>
          </Tab>

          <Tab eventKey="learners" title="Learner Profiles">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Learner Progress Overview</h5>
                <Button
                  variant="success"
                  size="sm"
                  onClick={exportLoginReport}
                >
                  📊 Export Login Report
                </Button>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Program</th>
                      <th>Last Sign-In</th>
                      <th>Module Progress</th>
                      <th>Assignment Progress</th>
                      <th>Document Status</th>
                      <th>Outstanding Items</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.map((learner) => {
                      const progress = getLearnerProgress(learner);

                      // Format last login time
                      const formatLastLogin = (lastLoginAt, createdAt) => {
                        if (!lastLoginAt) return { text: 'Never', variant: 'secondary', color: 'text-muted' };

                        const lastLogin = lastLoginAt.toDate ? lastLoginAt.toDate() : new Date(lastLoginAt);

                        // Validate that lastLogin is a valid date
                        if (isNaN(lastLogin.getTime())) {
                          return { text: 'Invalid Date', variant: 'secondary', color: 'text-muted' };
                        }

                        // NOTE: Removed the createdAt proximity check that was causing false "Never" readings
                        // Students who sign up and immediately log in were being marked as "Never"
                        // The lastLoginAt field is now maintained by AuthContext on every login

                        const now = new Date();
                        const diffMs = now - lastLogin;

                        // Handle future dates (clock skew)
                        if (diffMs < 0) return { text: 'Just now', variant: 'success', color: 'text-success' };

                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMinutes = Math.floor(diffMs / (1000 * 60));

                        if (diffMinutes < 1) return { text: 'Just now', variant: 'success', color: 'text-success' };
                        if (diffMinutes < 60) return { text: `${diffMinutes}m ago`, variant: 'success', color: 'text-success' };
                        if (diffHours < 24) return { text: `${diffHours}h ago`, variant: 'success', color: 'text-success' };
                        if (diffDays === 1) return { text: 'Yesterday', variant: 'info', color: 'text-info' };
                        if (diffDays < 7) return { text: `${diffDays}d ago`, variant: 'info', color: 'text-info' };
                        if (diffDays < 30) return { text: `${diffDays}d ago`, variant: 'warning', color: 'text-warning' };
                        return { text: `${diffDays}d ago`, variant: 'danger', color: 'text-danger' };
                      };

                      const lastLoginInfo = formatLastLogin(learner.lastLoginAt, learner.createdAt);

                      return (
                        <tr key={learner.id || learner.uid} className={progress.hasOutstandingItems ? 'table-warning' : ''}>
                          <td>{learner.displayName}</td>
                          <td>{learner.email}</td>
                          <td>{getProgramName(learner)}</td>
                          <td>
                            <Badge bg={lastLoginInfo.variant}>
                              {lastLoginInfo.text}
                            </Badge>
                          </td>
                          <td>
                            <ProgressBar
                              now={progress.moduleCompletionRate}
                              label={`${progress.moduleCompletionRate}%`}
                              variant="info"
                              size="sm"
                            />
                          </td>
                          <td>
                            <ProgressBar
                              now={progress.assignmentCompletionRate}
                              label={`${progress.assignmentCompletionRate}%`}
                              variant="success"
                              size="sm"
                            />
                          </td>
                          <td>
                            <ProgressBar
                              now={progress.documentCompletionRate}
                              label={`${progress.documentCompletionRate}%`}
                              variant={progress.documentCompletionRate === 100 ? 'success' : progress.documentCompletionRate > 50 ? 'warning' : 'danger'}
                              size="sm"
                            />
                          </td>
                          <td>
                            {progress.hasOutstandingItems ? (
                              <div>
                                {progress.outstandingDocuments > 0 && (
                                  <Badge bg="warning" className="me-1">
                                    {progress.outstandingDocuments} Missing
                                  </Badge>
                                )}
                                {progress.rejectedDocuments > 0 && (
                                  <Badge bg="danger" className="me-1">
                                    {progress.rejectedDocuments} Rejected
                                  </Badge>
                                )}
                                {progress.pendingDocuments > 0 && (
                                  <Badge bg="info">
                                    {progress.pendingDocuments} Pending
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <Badge bg="success">Complete</Badge>
                            )}
                          </td>
                          <td>
                            <Button variant="primary" size="sm" onClick={() => navigate(`/learner/${learner.id || learner.uid}`)}>
                              View Profile
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="learner-lookup" title="Learner Lookup">
            <FacilitatorLearnerLookup learners={learners} />
          </Tab>

          <Tab eventKey="resources" title="📚 Resources">
            <Card>
              <Card.Header>
                <strong>Learning Resources</strong>
                <Badge bg="secondary" className="ms-2">{resources.length} resources</Badge>
              </Card.Header>
              <Card.Body>
                {resources.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <strong>No resources available.</strong>
                    <p className="mb-0 mt-2">
                      Resources for your assigned programmes and cohorts will appear here once uploaded.
                    </p>
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover size="sm">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Uploaded</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resources.map(resource => (
                          <tr key={resource.id}>
                            <td className="text-center" style={{ width: '40px' }}>
                              <span style={{ fontSize: '1.2rem' }}>
                                {getResourceIcon(resource.category, resource.type)}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong>{resource.title}</strong>
                                {resource.description && (
                                  <div className="small text-muted">{resource.description.substring(0, 100)}...</div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge bg="secondary">{resource.category}</Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {resource.uploadedAt ? resource.uploadedAt.toLocaleDateString() : 'N/A'}
                              </small>
                            </td>
                            <td>
                              {resource.isExternalLink ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open Link
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  href={resource.fileURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="document-upload" title="Document Upload">
            <Card>
              <Card.Header>
                <h5>Upload Documents for Learners</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Control
                      type="text"
                      placeholder="Search learners by name, email, or ID number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Button variant="primary" onClick={handleSearchLearners}>
                      Search Learners
                    </Button>
                  </Col>
                </Row>

                {searchResults.length > 0 && (
                  <Card className="mb-3">
                    <Card.Header>Search Results</Card.Header>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>ID Number</th>
                            <th>Program</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((learner) => (
                            <tr key={learner.id}>
                              <td>{learner.displayName}</td>
                              <td>{learner.email}</td>
                              <td>{formatIdNumber(learner.idNumber)}</td>
                              <td>{getProgramName(learner)}</td>
                              <td>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleSelectLearnerForUpload(learner)}
                                >
                                  Upload Document
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}

                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">Search Submitted Documents</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2 align-items-end">
                      <Col md={4}>
                        <Form.Label>Program</Form.Label>
                        <Form.Select
                          value={documentProgramFilter}
                          onChange={(e) => {
                            setDocumentProgramFilter(e.target.value);
                            setDocumentCohortFilter('');
                          }}
                        >
                          <option value="">All programs</option>
                          {getScopedPrograms().map((program) => (
                            <option key={program.id} value={program.id}>
                              {program.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label>Cohort</Form.Label>
                        <Form.Select
                          value={documentCohortFilter}
                          onChange={(e) => setDocumentCohortFilter(e.target.value)}
                        >
                          <option value="">All cohorts</option>
                          {getScopedCohorts().map((cohort) => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name || cohort.cohortName}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label>Search learner</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Name, email, or ID..."
                          value={documentSearchTerm}
                          onChange={(e) => setDocumentSearchTerm(e.target.value)}
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                      <Button variant="outline-primary" onClick={handleSearchDocuments} disabled={documentSearchLoading}>
                        {documentSearchLoading ? 'Searching...' : 'Search Documents'}
                      </Button>
                    </div>
                    {documentSearchError && (
                      <Alert variant="warning" className="mt-3 mb-0">
                        {documentSearchError}
                      </Alert>
                    )}
                    {!documentSearchLoading && documentSearchResults.length > 0 && (
                      <div className="mt-3">
                        <Table striped bordered hover responsive size="sm">
                          <thead>
                            <tr>
                              <th>Learner</th>
                              <th>Program</th>
                              <th>Cohort</th>
                              <th>Document</th>
                              <th>Status</th>
                              <th>Review Notes</th>
                              <th>Submitted By</th>
                              <th>Uploaded</th>
                              <th>File</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentSearchResults.map((docItem) => (
                              <tr key={`${docItem.learnerId}-${docItem.id}`}>
                                <td>
                                  <div>{docItem.learnerName}</div>
                                  <div className="small text-muted">{docItem.learnerEmail}</div>
                                </td>
                                <td>{docItem.programName}</td>
                                <td>{docItem.cohortName}</td>
                                <td>{docItem.category}</td>
                                <td>
                                  <Badge bg={docItem.status === 'verified' ? 'success' : docItem.status === 'rejected' ? 'danger' : 'warning'}>
                                    {docItem.status}
                                  </Badge>
                                </td>
                                <td style={{ maxWidth: '220px' }}>
                                  {docItem.rejectionReason || docItem.reviewNotes ? (
                                    <div>
                                      {docItem.rejectionReason && (
                                        <div><strong>Reason:</strong> {docItem.rejectionReason}</div>
                                      )}
                                      {docItem.reviewNotes && (
                                        <div className="small text-muted">{docItem.reviewNotes}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  {docItem.uploadedByRole || 'learner'}
                                  {docItem.uploadedByEmail && (
                                    <div className="small text-muted">{docItem.uploadedByEmail}</div>
                                  )}
                                </td>
                                <td>
                                  {docItem.uploadedAt ? new Date(docItem.uploadedAt).toLocaleDateString('en-ZA') : 'N/A'}
                                </td>
                                <td>
                                  {docItem.fileURL ? (
                                    <Button size="sm" variant="outline-primary" href={docItem.fileURL} target="_blank" rel="noopener noreferrer">
                                      View
                                    </Button>
                                  ) : (
                                    <span className="text-muted">No file</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    {!documentSearchLoading && documentSearchResults.length === 0 && documentSearchTerm.trim().length >= 2 && (
                      <Alert variant="light" className="mt-3 mb-0">
                        No documents found for this search.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>

                <h6>Quick Access - Your Assigned Learners</h6>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Program</th>
                      <th>Document Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.slice(0, 10).map((learner) => {
                      const progress = getLearnerProgress(learner);
                      return (
                        <tr key={learner.id || learner.uid}>
                          <td>{learner.displayName}</td>
                          <td>{learner.email}</td>
                          <td>{getProgramName(learner)}</td>
                          <td>
                            <ProgressBar
                              now={progress.documentCompletionRate}
                              label={`${progress.documentCompletionRate}%`}
                              variant={progress.documentCompletionRate === 100 ? 'success' : progress.documentCompletionRate > 50 ? 'warning' : 'danger'}
                              size="sm"
                            />
                          </td>
                          <td>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSelectLearnerForUpload(learner)}
                            >
                              Upload Document
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="reports" title="Progress Reports">
            <ProgressReportGenerator />
          </Tab>

          <Tab eventKey="daily-reports" title="Daily Reports">
            <DailyReportLog />
          </Tab>

          <Tab eventKey="module-builder" title="Module Builder">
            <ModuleBuilder />
          </Tab>

          <Tab eventKey="course-builder" title="Course Builder">
            <CourseBuilder />
          </Tab>

          <Tab eventKey="session-logs" title="Session Logs">
            <SessionLogger />
          </Tab>
          
          <Tab eventKey="cohorts" title="Cohort Management">
            <FacilitatorCohortManagement />
          </Tab>
          
          <Tab eventKey="quiz-builder" title="Quiz Builder">
            <QuizBuilder />
          </Tab>

          <Tab eventKey="issues" title="Issues">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5>Recent Issues</h5>
                  <Button variant="primary" size="sm" onClick={() => navigate('/admin/issues')}>
                    View All Issues
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Reporter</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.slice(0, 5).map((issue) => (
                      <tr key={issue.id}>
                        <td>{issue.id}</td>
                        <td>{issue.title}</td>
                        <td>
                          <Badge bg={issue.priority === 'high' ? 'danger' : issue.priority === 'medium' ? 'warning' : 'success'}>
                            {issue.priority}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={issue.status === 'resolved' ? 'success' : issue.status === 'in_progress' ? 'warning' : 'primary'}>
                            {issue.status}
                          </Badge>
                        </td>
                        <td>{issue.reportedByName}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" onClick={() => navigate('/admin/issues')}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
        </>
        )}
      </Container>

      {/* Document Upload Modal */}
      <Modal show={showUploadModal} onHide={handleCloseUploadModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload Document for {selectedLearner?.displayName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && (
            <Alert variant="danger" className="mb-3">
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert variant="success" className="mb-3">
              {uploadSuccess}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Learner Information</Form.Label>
                <Form.Control
                  type="text"
                  value={`${selectedLearner?.displayName} (${selectedLearner?.email})`}
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ID Number</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedLearner?.idNumber || 'Not provided'}
                  disabled
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Document Category *</Form.Label>
                <Form.Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Select document category...</option>
                  {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => (
                    <option key={key} value={key}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Select File *</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Add any notes about this document upload..."
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
            />
          </Form.Group>

          <div className="text-muted">
            <small>
              <strong>Allowed file types:</strong> PDF, Word documents (.doc, .docx), Images (.jpg, .jpeg, .png)<br/>
              <strong>Maximum file size:</strong> 10MB
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseUploadModal} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleFileUpload}
            disabled={uploading || !selectedLearner || !uploadFile || !selectedCategory}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FacilitatorDashboard;
